import { EmailTemplate } from '../EmailTemplates/emailTemplateModel'
import { AutomationSchedule } from '../../domain/proposals/settings/proposalSettings'

export type SendProposalState = {
  recipients: string[]
  ccRecipients: string
  addReplyToAddress: boolean
  replyToAddresses: string
  selectedEmailTemplate: EmailTemplate
  emailSubject: string
  emailBody: string
  sendPdf: boolean
  notifySalesPerson: boolean
  automatedSchedule: AutomationSchedule | null
}

export type SendProposalDefaults = {
  template: EmailTemplate
  salesPersonEmail?: string
  sendProposalPdfByDefault: boolean
  notifySalesPersonByDefault: boolean
}

export const initialSendState = (defaults: SendProposalDefaults): SendProposalState => ({
  recipients: [],
  ccRecipients: '',
  addReplyToAddress: true,
  replyToAddresses: defaults.salesPersonEmail ?? '',
  selectedEmailTemplate: defaults.template,
  emailSubject: defaults.template.emailSubject ?? '',
  emailBody: defaults.template.emailBody ?? '',
  sendPdf: defaults.sendProposalPdfByDefault,
  notifySalesPerson: defaults.notifySalesPersonByDefault,
  automatedSchedule: null
})

export const proposalLinkToken = '{{proposal_link}}'

export const hasProposalLink = (emailBody: string): boolean =>
  emailBody.toLowerCase().includes(proposalLinkToken)

export const parseEmailList = (value: string): string[] =>
  value
    .split(',')
    .map(x => x.trim())
    .filter(x => x.length > 0)

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const invalidEmails = (value: string): string[] =>
  parseEmailList(value).filter(x => !emailPattern.test(x))

export const sendValidationErrors = (state: SendProposalState): string[] => {
  const errors: string[] = []
  if (state.recipients.length < 1) {
    errors.push('At least one recipient must be selected.')
  }
  if (!state.addReplyToAddress || parseEmailList(state.replyToAddresses).length < 1) {
    errors.push('At least one reply-to address must be provided.')
  }
  if (invalidEmails(state.replyToAddresses).length > 0) {
    errors.push(`Not a valid email address: ${invalidEmails(state.replyToAddresses).join(', ')}`)
  }
  if (invalidEmails(state.ccRecipients).length > 0) {
    errors.push(`Not a valid CC address: ${invalidEmails(state.ccRecipients).join(', ')}`)
  }
  if (!hasProposalLink(state.emailBody)) {
    errors.push(`The email body must contain ${proposalLinkToken} at least once.`)
  }
  return errors
}

export const canSend = (state: SendProposalState): boolean => sendValidationErrors(state).length === 0
